/**
 * The Dead Drop — daily Semantle-style word hunt.
 *
 * A single archive-themed secret word rotates at UTC midnight (same for the
 * whole community). Players guess any English word; Gemini scores each
 * guess 0-100 for semantic relatedness. Solving (score == 100) auto-mints
 * a high-tier Vault Card (mythic / classified / divine / celestial only)
 * and the reward modal pops up immediately — no extra click required.
 *
 * Hints exposed every day: category (e.g. SURVEILLANCE), subtype
 * (PLACE / PERSON / OPERATION / OBJECT / EVENT / ORG / TERM / CRYPTID),
 * word length, and the first letter. Unlimited tries, no give-up button.
 */
import React, { useEffect, useState, useRef, useMemo } from "react";
import { Send, Loader2, FileText, KeyRound, Hash, MapPin, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import CardRewardModal from "./CardRewardModal";

const LABEL_COLOR = {
  EXACT:   "#00ff88",
  BURNING: "#ff3b30",
  HOT:     "#ff8c00",
  WARM:    "#ffd60a",
  COOL:    "#5fb4ff",
  COLD:    "#94a3b8",
  FROZEN:  "#475569",
};

const LABEL_GLYPH = {
  EXACT:   "✓",
  BURNING: "🔥",
  HOT:     "▲",
  WARM:    "◆",
  COOL:    "◇",
  COLD:    "▽",
  FROZEN:  "·",
};

export default function DeadDrop() {
  const { user } = useAuth();
  const [puzzle, setPuzzle] = useState(null);
  const [progress, setProgress] = useState({ guesses: [], won: false, best_score: 0, reward: null });
  const [guess, setGuess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [sortMode, setSortMode] = useState("score"); // "score" | "order"
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get("/dead-drop/today");
      setPuzzle(r.data);
      if (r.data.my_progress) {
        setProgress(r.data.my_progress);
      }
    } catch (e) {
      // Non-fatal — homepage component should never explode if backend hiccups.
      console.warn("dead-drop load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [user?.id]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const w = (guess || "").trim().toLowerCase();
    if (!w || submitting) return;
    if (!user) {
      toast.error("Sign in to play The Dead Drop");
      return;
    }
    if (!/^[a-z][a-z0-9\-']{1,29}$/.test(w)) {
      toast.error("Guesses must be 2-30 letters");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/dead-drop/guess", { word: w });
      const d = r.data;
      setGuess("");
      // Optimistically append to the progress list (dedupe duplicates).
      setProgress((prev) => {
        const next = (prev.guesses || []).filter((g) => g.word !== d.word);
        next.push({ word: d.word, score: d.score, label: d.label, ts: new Date().toISOString() });
        return {
          ...prev,
          guesses: next,
          best_score: Math.max(prev.best_score || 0, d.score),
          won: prev.won || !!d.won,
          reward: d.reward || prev.reward,
        };
      });
      if (d.won && d.reward) {
        // Per user explicit request — the reward MUST pop up automatically
        // on solve. No "tap to reveal" step. Defer one tick so the guess
        // list visibly updates first, then the modal slams in.
        setTimeout(() => setRewardOpen(true), 250);
      } else if (d.duplicate) {
        toast(`Already tried — ${d.score} (${d.label})`);
      }
      inputRef.current?.focus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't score that guess");
    } finally {
      setSubmitting(false);
    }
  };

  const sortedGuesses = useMemo(() => {
    const arr = (progress.guesses || []).slice();
    if (sortMode === "score") arr.sort((a, b) => (b.score || 0) - (a.score || 0));
    else arr.reverse();   // newest first
    return arr;
  }, [progress.guesses, sortMode]);

  if (loading) {
    return (
      <div className="border border-white/10 bg-black p-6 font-mono text-[11px] text-zinc-500 flex items-center gap-2" data-testid="dead-drop-loading">
        <Loader2 className="animate-spin" size={14} /> Loading dead drop…
      </div>
    );
  }
  if (!puzzle) {
    return (
      <div className="border border-white/10 bg-black p-6 font-mono text-[11px] text-zinc-500" data-testid="dead-drop-empty">
        Dead drop unavailable right now.
      </div>
    );
  }

  const won = progress.won;

  return (
    <div
      className="relative border border-[#ff3b30]/40 bg-gradient-to-br from-[#170a0a] to-black overflow-hidden"
      data-testid="dead-drop-card"
    >
      {/* Header bar */}
      <div className="border-b border-[#ff3b30]/30 px-5 py-3 flex items-center justify-between bg-[#0a0505]">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#ff3b30] flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff3b30] animate-pulse" />
            // THE DEAD DROP · DAY {puzzle.day_index}
          </div>
          <div className="font-heading font-black uppercase tracking-tight text-white text-xl sm:text-2xl mt-0.5">
            Hot / Cold. One word.
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          title="Refresh"
          className="text-zinc-500 hover:text-white p-1"
          data-testid="dead-drop-refresh"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Hint strip — category + subtype + length + first letter */}
      <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2 border-b border-white/5">
        <HintChip icon={<FileText size={11} />} label="CATEGORY" value={puzzle.category} />
        <HintChip icon={<MapPin size={11} />}   label="TYPE"     value={puzzle.subtype} />
        <HintChip icon={<KeyRound size={11} />} label="STARTS"   value={`"${puzzle.first_letter}"`} />
        <HintChip icon={<Hash size={11} />}     label="LENGTH"   value={`${puzzle.length} LETTERS`} />
      </div>

      {/* Solve state banner */}
      {won ? (
        <div className="px-5 py-3 bg-[#00ff88]/8 border-b border-[#00ff88]/30 flex items-center justify-between">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#00ff88]">
            // CRACKED in {progress.guesses?.length || 0} guess{(progress.guesses?.length || 0) === 1 ? "" : "es"}
          </div>
          {progress.reward ? (
            <button
              type="button"
              onClick={() => setRewardOpen(true)}
              className="font-mono text-[10px] uppercase tracking-[0.22em] border border-[#00ff88]/60 text-[#00ff88] px-2 py-1 hover:bg-[#00ff88]/10"
              data-testid="dead-drop-view-reward"
            >
              View reward
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Guess input */}
      {!won ? (
        <form onSubmit={submit} className="px-5 py-4 flex items-center gap-2 border-b border-white/5">
          <input
            ref={inputRef}
            type="text"
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            placeholder="Type a word and submit…"
            maxLength={30}
            disabled={submitting || !user}
            className="flex-1 bg-black border border-white/15 text-white px-3 py-2 font-mono text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff3b30]"
            data-testid="dead-drop-input"
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={submitting || !guess || !user}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#ff3b30] text-black font-mono text-[10px] uppercase tracking-[0.22em] font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#ff5747] transition-colors"
            data-testid="dead-drop-submit"
          >
            {submitting ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />}
            Drop
          </button>
        </form>
      ) : null}

      {/* Sign-in nudge */}
      {!user ? (
        <div className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 border-b border-white/5">
          Sign in to play and collect the reward card.
        </div>
      ) : null}

      {/* Guess history */}
      <div className="px-5 py-4">
        {progress.guesses?.length ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                {progress.guesses.length} guess{progress.guesses.length === 1 ? "" : "es"} · best {progress.best_score}
              </div>
              <div className="flex border border-white/10 text-[9px] font-mono uppercase tracking-widest">
                {["score", "order"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setSortMode(m)}
                    className="px-2 py-1"
                    style={sortMode === m
                      ? { background: "#ff3b30", color: "#000" }
                      : { background: "transparent", color: "rgba(255,255,255,0.5)" }
                    }
                    data-testid={`dead-drop-sort-${m}`}
                  >
                    {m === "score" ? "By heat" : "By order"}
                  </button>
                ))}
              </div>
            </div>
            <ul className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {sortedGuesses.map((g, i) => (
                <GuessRow key={`${g.word}-${i}`} g={g} />
              ))}
            </ul>
          </>
        ) : (
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600 py-3 text-center border border-dashed border-white/10">
            // no guesses yet — try anything related to {puzzle.category.toLowerCase()}
          </div>
        )}
      </div>

      {/* Reward modal — pops up automatically on solve */}
      {rewardOpen && progress.reward ? (
        <CardRewardModal
          reward={progress.reward}
          onClose={() => setRewardOpen(false)}
        />
      ) : null}
    </div>
  );
}

function HintChip({ icon, label, value }) {
  return (
    <div className="border border-white/10 bg-black/40 px-2 py-1.5">
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="font-heading font-black uppercase tracking-tight text-white text-[13px] leading-tight mt-0.5 truncate">
        {value}
      </div>
    </div>
  );
}

function GuessRow({ g }) {
  const color = LABEL_COLOR[g.label] || "#94a3b8";
  const pct = Math.max(0, Math.min(100, g.score || 0));
  return (
    <li
      className="flex items-center gap-3 border border-white/5 bg-black/40 px-2 py-1.5 relative overflow-hidden"
      data-testid={`dead-drop-guess-${g.word}`}
    >
      {/* heat bar background */}
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 opacity-15"
        style={{ width: `${pct}%`, background: color }}
      />
      <span className="font-mono text-[12px] text-white relative z-10 flex-1 truncate">
        {g.word}
      </span>
      <span
        className="font-mono text-[10px] uppercase tracking-[0.22em] relative z-10"
        style={{ color }}
      >
        <span className="mr-1">{LABEL_GLYPH[g.label] || ""}</span>
        {g.label}
      </span>
      <span
        className="font-mono text-[12px] tabular-nums relative z-10 w-10 text-right font-bold"
        style={{ color }}
      >
        {g.score}
      </span>
    </li>
  );
}
